<template>
  <div class="photoelectric-work-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$h · f_c = W$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>入射光频率 f</label>
            <span class="val">{{ freq.toFixed(1) }} <small>×10¹⁴ Hz</small></span>
          </div>
          <div class="hud-item orange">
            <label>金属逸出功 W</label>
            <span class="val">{{ workFunction.toFixed(2) }} <small>eV</small></span>
          </div>
          <div class="hud-item" :class="isEmitting ? 'green' : 'gray'">
             <label>光电效应</label>
             <span class="val">{{ isEmitting ? '激发中' : '能量不足 (截止)' }}</span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 450 240" preserveAspectRatio="xMidYMid meet">
          <!-- 金属表面 (Metal Surface) -->
          <rect x="150" y="180" width="150" height="20" fill="#475569" stroke="#94a3b8" />
          <text x="225" y="215" text-anchor="middle" fill="#64748b" font-size="9">金属阴极（当前：{{ materialName }}）</text>

          <!-- 入射光子 (Incoming Photons) -->
          <g v-for="i in 3" :key="'p'+i">
             <path :d="`M ${50+i*20},40 L ${150+i*20},180`" fill="none" :stroke="photonColor" stroke-width="2" />
             <circle :cx="150+i*20" :cy="180" r="4" :fill="photonColor" filter="url(#glow-p)" />
          </g>

          <!-- 逸出的电子 (Ejected Electrons) -->
          <g v-if="isEmitting">
             <circle v-for="e in electrons" :key="e.id" :cx="e.x" :cy="e.y" r="4" fill="#fbbf24" filter="url(#glow-p)" />
          </g>

          <defs>
             <filter id="glow-p"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>
        </svg>

        <div class="physics-caption">
           规律：光电子的产生只取决于光的频率而非强度。当光子能量小于金属的逸出功 $W$ 时，无法产生电子。
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">调节入射光频率 (能量)：{{ freq.toFixed(1) }}</label>
                 <el-slider v-model="freq" :min="1.0" :max="10.0" :step="0.1" size="small"></el-slider>
              </div>
              <div class="flex-1">
                 <label class="s-label">选择金属材料 (逸出功 W)：</label>
                 <el-select v-model="material" size="mini">
                    <el-option label="铯 (Cs) - 1.9eV" :value="1.9"></el-option>
                    <el-option label="钠 (Na) - 2.3eV" :value="2.3"></el-option>
                    <el-option label="铝 (Al) - 4.1eV" :value="4.1"></el-option>
                    <el-option label="白金 (Pt) - 6.3eV" :value="6.3"></el-option>
                 </el-select>
              </div>
           </div>
        </div>
        
        <!-- 高对比度蓝色专业卡片加固 -->
        <div class="discovery-card-discovery-tir" v-latex>
           <div class="icon-wrap"><i class="el-icon-odometer"></i></div>
           <div class="text-content">
             <p><strong>实战物理/光电探测：</strong>逸出功是每个原子的“忠诚度”。<strong>解惑：</strong>为什么低频光照不亮？<strong>观察：</strong>调节频率滑块。你会发现当光变成蓝色或紫外线时，电子才猛然跳出。如果入射能量不够，哪怕你把光强开到 100 倍，金属表面也纹丝不动。这一发现让爱因斯坦赢得了诺贝尔奖。
             </p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'PhotoelectricWorkFunctionProViz',
  data() {
    return {
      freq: 3.5,
      material: 2.3, // Sodium
      electrons: [],
      nextId: 0
    }
  },
  computed: {
    workFunction() { return this.material; },
    isEmitting() {
       // h*f (h in eV*s unit is 4.135e-15)
       // Simplified frequency to match eV units.
       return this.freq * 0.7 > this.workFunction;
    },
    materialName() {
       const map = { 1.9: '铯 (Cs)', 2.3: '钠 (Na)', 4.1: '铝 (Al)', 6.3: '白金 (Pt)' };
       return map[this.material];
    },
    photonColor() {
       if (this.freq < 4) return "#dc2626"; // Red
       if (this.freq < 6) return "#fbbf24"; // Yellow
       if (this.freq < 8) return "#0ea5e9"; // Blue
       return "#a855f7"; // UV
    }
  },
  mounted() { this.animate(); },
  methods: {
    animate() {
       if (this.isEmitting && Math.random() < 0.1) {
          this.electrons.push({ id: this.nextId++, x: 225 + (Math.random()-0.5)*100, y: 180, vy: -3 - Math.random()*2 });
       }
       this.electrons.forEach((e, i) => {
          e.y += e.vy;
          if (e.y < 20) this.electrons.splice(i, 1);
       });
       this.animFrame = requestAnimationFrame(this.animate);
    }
  },
  beforeDestroy() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    if (this.timer) clearInterval(this.timer);
  },
}
</script>

<style scoped lang="scss">
@import "./PhysicsCommon.scss";
.photoelectric-work-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
</style>
